import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Save, Calendar, Target, Users, BarChart3, Plus, Trash2, Loader2, Check, Pencil, Eye, AlertTriangle, TrendingUp, CheckCircle2, Circle, Layers, FileText, Paperclip, MessageSquare, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { MarkdownEditor } from '../ui/markdown-editor';
import { epicsApi, initiativesApi, collectionActionsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags, FEATURE_IDS } from '../../hooks/useFeatureFlags';
import { useEntityModal } from '../../contexts/EntityModalContext';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { UnifiedCoPilot } from '../copilot';
import KpiSubscriptionTab from '../shared/KpiSubscriptionTab';
import { NotesPanel, AttachmentsPanel } from '../shared';

// Status options (matches Initiative - gold standard)
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'planning', label: 'Planning', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
  { value: 'shipped', label: 'Shipped', color: 'bg-green-500' },
  { value: 'paused', label: 'Paused', color: 'bg-purple-500' },
];

// Valid status values - used to normalize legacy/invalid statuses
const VALID_STATUSES = STATUS_OPTIONS.map(o => o.value);

// Normalize status to a valid value (fallback to 'draft' for unknown/legacy statuses like "active")
const normalizeStatus = (status) => {
  return VALID_STATUSES.includes(status) ? status : 'draft';
};

/**
 * EpicDetailModal
 * 
 * Displays and allows editing of epic details with features tab.
 * 
 * STANDALONE MODE:
 * When `standalone={true}`, this modal operates independently without requiring
 * parent callbacks. Used by Executive AI to open epics in-place without
 * navigating away. In standalone mode:
 * - onUpdate callback is ignored (API is called directly, toast shown)
 * - Modal closes automatically after save/delete
 * - No parent state refresh needed
 */
const EpicDetailModal = ({
  isOpen,
  epic,
  onSave,
  onSaveAndClose,
  onClose,
  onDelete,
  onFeatureChange,  // Called when features are added/deleted
  onKpiChange,      // Called when KPI subscriptions change (for cascading refresh)
  productContext = {},
  personas = [],
  onSelectFeature = null,  // Callback to open feature modal OVER this modal
  onChildFeatureUpdated = null,  // Callback ref setter — parent calls this to patch a single feature
  standalone = false,  // When true, modal operates independently without parent callbacks
  zIndex = 50,  // Z-index for modal stacking
  suppressChildModals = false,  // When true, hides AI copilot and prevents entity edit modals from launching as children
  parentRefresh,  // Parent's refresh callbacks — when present, this modal is a child and calls these after save
}) => {
  const { getThemeColors, getActivePersonality } = useSafeMode();
  const { canCrudProducts, canUseAI } = usePermissions();
  const { isEnabled } = useFeatureFlags();
  const { user } = useAuth();
  const colors = getThemeColors();
  const activePersonality = getActivePersonality();
  const { registerParentCallbacks, unregisterParentCallbacks } = useEntityModal();
  
  // Dirty collection tracking — same pattern as InitiativeDetailModal.
  // Child feature saves mark 'features' dirty. Flushed on modal close.
  const dirtyCollectionsRef = useRef(new Set());
  
  // Helper function for relative timestamps
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Permission flags
  const canEdit = canCrudProducts;
  const canUseAICopilot = canUseAI;

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('key_attributes');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft',
    problem_statement: '',
    target_audience: '',
    strategic_goal: '',
    target_date: '',
    persona_ids: [],
    subscribed_kpi_ids: []
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [epicDetails, setEpicDetails] = useState(null);
  
  // Track if KPI subscriptions were modified during this edit session (for cascading refresh)
  const [kpisModified, setKpisModified] = useState(false);
  const [health, setHealth] = useState(null);
  const [features, setFeatures] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Pending cross-entity child updates from AI copilot (e.g., feature updates from epic)
  const [pendingChildUpdates, setPendingChildUpdates] = useState([]);

  // Expose a targeted feature patch function to the parent via callback
  const patchFeature = useCallback((savedFeature) => {
    if (!savedFeature?.feature_id) return;
    setFeatures(prev => prev.map(f => 
      f.feature_id === savedFeature.feature_id ? { ...f, ...savedFeature } : f
    ));
  }, []);

  useEffect(() => {
    if (onChildFeatureUpdated) onChildFeatureUpdated(patchFeature);
  }, [onChildFeatureUpdated, patchFeature]);

  const [deleting, setDeleting] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  
  // KPI override data - used when user changes Initiative before saving
  // This allows the KPI tab to show KPIs from the newly selected Initiative
  const [kpiOverrideData, setKpiOverrideData] = useState(null);
  const [loadingKpiOverride, setLoadingKpiOverride] = useState(false);
  
  // Ephemeral KPI selections - used when viewing a different initiative (not saved yet)
  // These are discarded when switching initiatives or closing without save
  const [ephemeralKpiSelections, setEphemeralKpiSelections] = useState([]);

  // Computed: Show edit toggle? (when status !== draft and user can edit)
  const showEditToggle = canEdit && formData.status !== 'draft';
  
  // Computed: Is currently in editing state? (matches Initiative logic)
  const isEditing = canEdit && (formData.status === 'draft' || isEditMode);

  // Computed: Parent deadlines for calendar highlighting
  // Extracts unique goals with target_dates from inherited_kpis_by_goal
  const parentDeadlines = useMemo(() => {
    const deadlines = [];
    
    // Add goals from inherited KPIs (via subscribed KPIs)
    if (epicDetails?.inherited_kpis_by_goal?.length > 0) {
      epicDetails.inherited_kpis_by_goal.forEach(goalGroup => {
        if (goalGroup.goal_target_date) {
          deadlines.push({
            date: goalGroup.goal_target_date,
            label: `Goal: ${goalGroup.goal_title || 'Untitled Goal'}`,
            goal_id: goalGroup.goal_id,
          });
        }
      });
    }
    
    return deadlines;
  }, [epicDetails?.inherited_kpis_by_goal]);

  // Load epic details
  const loadEpicDetails = useCallback(async () => {
    if (!epic?.epic_id) return;
    try {
      const response = await epicsApi.getDetails(epic.epic_id);
      const details = response.data;
      setEpicDetails(details);
      setHealth(details.health);
      setFeatures(details.features || []);

      // Set form data (including initiative_id for controlled dropdown)
      // Normalize status to handle legacy/invalid values (e.g., "active" → "draft")
      const normalizedStatus = normalizeStatus(details.status);
      setFormData({
        title: details.title || '',
        description: details.description || '',
        status: normalizedStatus,
        problem_statement: details.problem_statement || '',
        target_audience: details.target_audience || '',
        strategic_goal: details.strategic_goal || '',
        target_date: details.target_date || '',
        persona_ids: details.persona_ids || [],
        subscribed_kpi_ids: details.subscribed_kpi_ids || [],
        initiative_id: details.initiative_id || ''
      });

      setHasChanges(false);
      setKpisModified(false);  // Reset KPI modification tracking
      // Set edit mode based on status: draft = edit mode, others = view mode (matches Initiative)
      // Use normalized status for the check
      setIsEditMode(normalizedStatus === 'draft');
    } catch (err) {
      console.error('Error loading epic details:', err);
      toast.error('Failed to load epic details');
    } finally {
      setLoading(false);
    }
  }, [epic?.epic_id]);

  // Refresh epic data without resetting edit mode (used after AI actions)
  const refreshEpicData = useCallback(async () => {
    if (!epic?.epic_id) return;
    try {
      const response = await epicsApi.getDetails(epic.epic_id);
      const details = response.data;
      setEpicDetails(details);
      setHealth(details.health);
      setFeatures(details.features || []);

      // Update form data (preserving edit mode)
      // Normalize status to handle legacy/invalid values
      setFormData({
        title: details.title || '',
        description: details.description || '',
        status: normalizeStatus(details.status),
        problem_statement: details.problem_statement || '',
        target_audience: details.target_audience || '',
        strategic_goal: details.strategic_goal || '',
        target_date: details.target_date || '',
        persona_ids: details.persona_ids || [],
        subscribed_kpi_ids: details.subscribed_kpi_ids || [],
        initiative_id: details.initiative_id || ''
      });
      // Note: NOT resetting hasChanges or isEditMode here
      // Also clear KPI override since we're refreshing from saved data
      setKpiOverrideData(null);
    } catch (err) {
      console.error('Error refreshing epic data:', err);
    }
  }, [epic?.epic_id]);

  // Standalone features refresh — used by parentRefresh registration
  const loadFeatures = useCallback(async () => {
    if (!epic?.epic_id) return;
    try {
      const response = await epicsApi.getDetails(epic.epic_id);
      setFeatures(response.data?.features || []);
    } catch (err) {
      console.error('Error refreshing features:', err);
    }
  }, [epic?.epic_id]);

  // Compute showAICopilot before registration effect
  const showAICopilot = canUseAICopilot && !suppressChildModals && epic?.epic_id && (formData.status === 'draft' || isEditMode);

  // Register parent refresh callbacks on context so co-pilot-linked child modals
  // can refresh this modal's local state instead of triggering full page refresh.
  useEffect(() => {
    if (showAICopilot && isOpen) {
      registerParentCallbacks({
        feature: loadFeatures,
        markDirty: (collection) => dirtyCollectionsRef.current.add(collection),
      });
    }
    return () => unregisterParentCallbacks();
  }, [showAICopilot, isOpen, registerParentCallbacks, unregisterParentCallbacks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (epic && isOpen) {
      setLoading(true);
      setActiveTab('key_attributes'); // Key Attributes is the default tab
      setShowDeleteConfirm(false); // Reset delete confirmation when switching epics
      setKpiOverrideData(null); // Reset KPI override when opening a new epic
      setEphemeralKpiSelections([]); // Clear ephemeral selections
      loadEpicDetails();
    }
  }, [epic, isOpen, loadEpicDetails]);

  // Watch for initiative_id changes and fetch new Initiative's KPIs
  // This enables real-time KPI updates when user changes the Initiative dropdown
  // 
  // Key UX rules:
  // - Source of truth = Epic's saved subscribed_kpi_ids tied to saved initiative_id
  // - All selections/deselections are ephemeral until Save
  // - Returning to original initiative restores persisted subscriptions
  // - Viewing different initiative shows that initiative's KPIs with no subscriptions
  // - "Not linked" shows empty state
  useEffect(() => {
    const handleInitiativeChange = async () => {
      const currentInitiativeId = formData.initiative_id || '';
      const originalInitiativeId = epicDetails?.initiative_id || '';
      
      // Case 1: Returning to original initiative - restore persisted subscriptions
      if (currentInitiativeId === originalInitiativeId) {
        setKpiOverrideData(null);
        setEphemeralKpiSelections([]); // Clear ephemeral selections
        // Restore original subscriptions from source of truth
        setFormData(prev => ({
          ...prev,
          subscribed_kpi_ids: epicDetails?.subscribed_kpi_ids || []
        }));
        return;
      }
      
      // Case 2: "Not linked" selected - show empty state
      if (!currentInitiativeId) {
        setEphemeralKpiSelections([]); // Clear ephemeral selections
        setKpiOverrideData({
          inherited_kpis: [],
          by_goal: [],
          total_available: 0,
          subscribed_count: 0,
          subscribed_kpi_ids: [],
          hint: "Link this Epic to an Initiative to inherit its subscribed KPIs"
        });
        return;
      }
      
      // Case 3: Different initiative selected - fetch its KPIs (all unchecked)
      setEphemeralKpiSelections([]); // Clear ephemeral selections for new initiative
      setLoadingKpiOverride(true);
      try {
        // Fetch the Initiative's inherited KPIs (which includes subscribed_kpi_ids)
        const response = await initiativesApi.getInheritedKpis(currentInitiativeId);
        const data = response.data;
        
        // Get the Initiative's subscribed KPIs (what Epic can inherit)
        const subscribedKpiIds = new Set(data.subscribed_kpi_ids || []);
        
        // Filter to only show subscribed KPIs (not all inherited ones)
        const filteredKpis = (data.inherited_kpis || []).filter(
          kpi => subscribedKpiIds.has(kpi.kpi_key)
        );
        
        // Filter by_goal to only include subscribed KPIs
        const filteredByGoal = (data.by_goal || []).map(goal => ({
          ...goal,
          kpis: goal.kpis.filter(kpi => subscribedKpiIds.has(kpi.kpi_key))
        })).filter(goal => goal.kpis.length > 0);
        
        // Find initiative title from productContext
        const newInitiative = productContext?.initiatives?.find(
          i => i.initiative_id === currentInitiativeId
        );
        
        // Build override data - all KPIs unchecked (ephemeral state)
        const overrideData = {
          inherited_kpis: filteredKpis,
          by_goal: filteredByGoal,
          total_available: filteredKpis.length,
          subscribed_count: 0,
          subscribed_kpi_ids: [], // No subscriptions for different initiative
          initiative: newInitiative ? {
            initiative_id: newInitiative.initiative_id,
            title: newInitiative.title
          } : null,
          hint: filteredKpis.length === 0 
            ? "This Initiative has no subscribed KPIs. Subscribe to KPIs in the Initiative first."
            : null
        };
        
        setKpiOverrideData(overrideData);
        
      } catch (err) {
        console.error('Error fetching KPIs for new Initiative:', err);
        setKpiOverrideData({
          inherited_kpis: [],
          by_goal: [],
          total_available: 0,
          subscribed_count: 0,
          subscribed_kpi_ids: [],
          hint: "Failed to load KPIs for this Initiative"
        });
      } finally {
        setLoadingKpiOverride(false);
      }
    };
    
    // Only run if we have epicDetails loaded (to compare against original initiative)
    if (epicDetails) {
      handleInitiativeChange();
    }
  }, [formData.initiative_id, epicDetails, productContext?.initiatives]);

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Handle applying updates from AI Co-Pilot
  const handleApplyUpdates = useCallback((updates) => {
    if (!updates) return;
    
    Object.entries(updates).forEach(([field, value]) => {
      // Handle pending child updates (cross-entity actions from copilot)
      if (field === '_pendingChildUpdates' && Array.isArray(value)) {
        setPendingChildUpdates(prev => {
          // Merge: replace existing entries for same item_id, add new ones
          const existing = new Map(prev.map(u => [u.item_id, u]));
          value.forEach(u => existing.set(u.item_id, u));
          return Array.from(existing.values());
        });
        setHasChanges(true);
        return;
      }
      
      // Handle persona - AI provides {id, name} objects or arrays of them
      if (field === 'persona_ids' && value) {
        // Normalize value to array
        const personaValues = Array.isArray(value) ? value : [value];
        const matchedIds = [];
        
        personaValues.forEach(v => {
          if (typeof v === 'object' && v.id) {
            // New format: {id: "persona_xxx", name: "..."}
            matchedIds.push(v.id);
          } else if (typeof v === 'string') {
            if (v.startsWith('persona_')) {
              // It's already an ID
              matchedIds.push(v);
            } else {
              // It's a name, try to match
              const matchedPersona = personas.find(p => 
                p.name?.toLowerCase().includes(v.toLowerCase()) ||
                v.toLowerCase().includes(p.name?.toLowerCase())
              );
              if (matchedPersona) {
                matchedIds.push(matchedPersona.persona_id);
              }
            }
          }
        });
        
        if (matchedIds.length > 0) {
          // Merge with existing persona_ids (don't replace)
          setFormData(prev => ({
            ...prev,
            persona_ids: [...new Set([...(prev.persona_ids || []), ...matchedIds])]
          }));
        }
      } else {
        // Regular field update
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    });
    
    setHasChanges(true);
  }, [personas]);

  // Process pending cross-entity child updates via API (partial commit)
  const processPendingChildUpdates = async () => {
    if (pendingChildUpdates.length === 0) return null;
    
    const childResults = { success: [], failed: [] };
    
    for (const childUpdate of pendingChildUpdates) {
      try {
        await collectionActionsApi.updateItem({
          product_id: productContext?.product_id || epic?.product_id,
          entity_type: childUpdate.entity_type,
          item_id: childUpdate.item_id,
          updates: childUpdate.updates,
        });
        childResults.success.push(childUpdate);
      } catch (err) {
        childResults.failed.push({ ...childUpdate, error: err.message || 'Update failed' });
      }
    }
    
    // Clear all pending regardless of outcome (partial commit model)
    setPendingChildUpdates([]);
    
    return childResults;
  };

  // Save epic - now passes saved data back to parent for entity refresh
  const handleSave = async () => {
    if (!epic?.epic_id) return;

    setSaving(true);
    try {
      // Build save data - use ephemeral selections if viewing different initiative
      const saveData = {
        ...formData,
        subscribed_kpi_ids: kpiOverrideData ? ephemeralKpiSelections : formData.subscribed_kpi_ids
      };
      
      const response = await epicsApi.update(epic.epic_id, saveData);
      setHasChanges(false);
      
      // Process pending child updates (cross-entity actions from copilot)
      const childResults = await processPendingChildUpdates();
      if (childResults) {
        if (childResults.failed.length > 0) {
          toast.warning(
            `Epic saved. ${childResults.success.length}/${childResults.success.length + childResults.failed.length} linked updates applied.`,
            { description: `Failed: ${childResults.failed.map(f => f.display_name).join(', ')}` }
          );
        } else if (childResults.success.length > 0) {
          toast.success(`Epic saved with ${childResults.success.length} linked update${childResults.success.length > 1 ? 's' : ''}`);
        } else {
          toast.success('Epic saved');
        }
      } else {
        toast.success('Epic saved');
      }
      
      // Clear KPI override and ephemeral selections since changes are now persisted
      setKpiOverrideData(null);
      setEphemeralKpiSelections([]);
      
      // Update epicDetails with saved data so initiative comparison works correctly
      if (response?.data) {
        setEpicDetails(prev => ({
          ...prev,
          ...response.data,
          initiative_id: response.data.initiative_id,
          subscribed_kpi_ids: response.data.subscribed_kpi_ids || []
        }));
        // Also update formData to reflect saved state
        setFormData(prev => ({
          ...prev,
          initiative_id: response.data.initiative_id || '',
          subscribed_kpi_ids: response.data.subscribed_kpi_ids || []
        }));
      }
      
      // Check for KPI integrity message and show as informational toast
      if (response?.data?.kpi_integrity_message) {
        toast.info(response.data.kpi_integrity_message, {
          duration: 8000,
          description: 'KPI subscriptions were updated to maintain hierarchy integrity.'
        });
      }
      
      // Notify parent — child mode uses parentRefresh, parent mode uses onSave
      if (!standalone) {
        if (parentRefresh?.epic) {
          parentRefresh.epic();
          parentRefresh.markDirty?.('epics');
          if (kpisModified) parentRefresh.markDirty?.('kpis');
          if (childResults?.success?.length > 0) {
            parentRefresh.feature?.();
            parentRefresh.markDirty?.('features');
          }
          onSave?.();
        } else if (onSave) {
          await onSave(response?.data, { kpisChanged: kpisModified, childUpdates: childResults });
        }
      }
      // Trigger cascade refresh if KPIs were modified
      if (kpisModified && onKpiChange) {
        onKpiChange();
      }
      setKpisModified(false);  // Reset after save
      
      // Refresh features list if child updates were made
      if (childResults && childResults.success.length > 0) {
        await loadEpicDetails();
      }
    } catch (err) {
      toast.error('Failed to save epic');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    if (!epic?.epic_id) return;

    setSaving(true);
    try {
      // Build save data - use ephemeral selections if viewing different initiative
      const saveData = {
        ...formData,
        subscribed_kpi_ids: kpiOverrideData ? ephemeralKpiSelections : formData.subscribed_kpi_ids
      };
      
      const response = await epicsApi.update(epic.epic_id, saveData);
      setHasChanges(false);
      
      // Process pending child updates (cross-entity actions from copilot)
      const childResults = await processPendingChildUpdates();
      
      // Clear KPI override and ephemeral selections since changes are now persisted
      setKpiOverrideData(null);
      setEphemeralKpiSelections([]);
      
      // Check for KPI integrity message and show as informational toast
      if (response?.data?.kpi_integrity_message) {
        toast.info(response.data.kpi_integrity_message, {
          duration: 8000,
          description: 'KPI subscriptions were updated to maintain hierarchy integrity.'
        });
      }
      
      // Notify parent — child mode uses parentRefresh, parent mode uses onSave
      if (!standalone) {
        if (parentRefresh?.epic) {
          parentRefresh.epic();
          parentRefresh.markDirty?.('epics');
          if (kpisModified) parentRefresh.markDirty?.('kpis');
          if (childResults?.success?.length > 0) {
            parentRefresh.feature?.();
            parentRefresh.markDirty?.('features');
          }
          if (onSaveAndClose) {
            onSaveAndClose();
          } else {
            onSave?.();
          }
        } else if (onSaveAndClose) {
          await onSaveAndClose(response?.data, { kpisChanged: kpisModified, childUpdates: childResults });
        } else if (onSave) {
          await onSave(response?.data, { kpisChanged: kpisModified, childUpdates: childResults });
        }
      }
      // Trigger cascade refresh if KPIs were modified
      if (kpisModified && onKpiChange) {
        onKpiChange();
      }
      setKpisModified(false);  // Reset after save
      
      // Toast with child update info
      if (childResults && childResults.failed.length > 0) {
        toast.warning(
          `Epic saved. ${childResults.success.length}/${childResults.success.length + childResults.failed.length} linked updates applied.`,
          { description: `Failed: ${childResults.failed.map(f => f.display_name).join(', ')}` }
        );
      } else if (childResults && childResults.success.length > 0) {
        toast.success(`Epic saved with ${childResults.success.length} linked update${childResults.success.length > 1 ? 's' : ''}`);
      } else {
        toast.success('Epic saved');
      }
      performClose();
    } catch (err) {
      toast.error('Failed to save epic');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Delete epic
  const handleDelete = async () => {
    if (!epic?.epic_id) return;

    setDeleting(true);
    try {
      await epicsApi.delete(epic.epic_id);
      toast.success('Epic deleted');
      
      // Notify parent to refresh collection
      if (!standalone && onDelete) {
        await onDelete();
      }
      onClose();
    } catch (err) {
      toast.error('Failed to delete epic');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  // Modal close handling - check for unsaved changes and pending AI actions
  const handleModalClose = () => {
    // First check for unsaved form changes or pending child updates
    if (hasChanges || pendingChildUpdates.length > 0) {
      setShowUnsavedWarning(true);
      return;
    }
    // No longer need close guard - pending actions persist in chat log
    performClose();
  };

  const performClose = () => {
    // Clear pending child updates on close
    setPendingChildUpdates([]);
    // Flush dirty collections to parent (deferred stale pattern)
    if (dirtyCollectionsRef.current.size > 0 && onSave) {
      onSave(null, { dirtyCollections: [...dirtyCollectionsRef.current], entityId: epic?.epic_id });
    }
    dirtyCollectionsRef.current.clear();
    onClose();
  };

  if (!isOpen) return null;

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'health', label: 'Health', icon: BarChart3 },
    { id: 'targets', label: 'Targets', icon: Target },
    { id: 'features', label: 'Features', icon: Layers },
  ];

  return (
    <ModalZIndexProvider zIndex={zIndex}>
    <div className="fixed inset-0" style={{ zIndex }}>
      {/* Backdrop */}
      <div className={cn("absolute inset-0 bg-black/60")} />

      {/* Modal */}
      <div className={cn(
        "absolute inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden",
        colors.bgCard
      )}>
        {/* Header */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
          <div className="flex items-center gap-3">
            <div>
              <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                {isEditing ? 'Edit Epic' : 'View Epic'}
              </h2>
              {formData.title && (
                <p className={cn("text-sm truncate max-w-[400px]", colors.textMuted)}>{formData.title}</p>
              )}
            </div>
            {/* View/Edit Toggle - Only show if status is NOT draft (matches Initiative) */}
            {showEditToggle && (
              <div className={cn("flex items-center rounded-lg p-1", colors.bgTertiary)}>
                <button
                  onClick={() => setIsEditMode(false)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5",
                    !isEditMode ? `${colors.bgCard} shadow-sm ${colors.textPrimary}` : `${colors.textMuted} hover:${colors.textPrimary}`
                  )}
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => setIsEditMode(true)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5",
                    isEditMode ? `${colors.bgCard} shadow-sm ${colors.textPrimary}` : `${colors.textMuted} hover:${colors.textPrimary}`
                  )}
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-2">
            {isEditing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                title="Delete epic"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || (!hasChanges && pendingChildUpdates.length === 0)}
              variant={(hasChanges || pendingChildUpdates.length > 0) ? "default" : "outline"}
              className={cn((hasChanges || pendingChildUpdates.length > 0) ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
              data-testid="epic-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-1">Save{pendingChildUpdates.length > 0 ? ` (+${pendingChildUpdates.length})` : ''}</span>
            </Button>
            <Button
              size="sm"
              onClick={handleSaveAndClose}
              disabled={saving || (!hasChanges && pendingChildUpdates.length === 0)}
              variant={(hasChanges || pendingChildUpdates.length > 0) ? "default" : "outline"}
              className={cn((hasChanges || pendingChildUpdates.length > 0) ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
              data-testid="epic-save-close-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-1">Save & Close</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleModalClose}
              className={cn(colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
            >
              Close
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Strategic Content (60% when copilot shown, full width otherwise) */}
            <div className={cn(
              "overflow-y-auto flex flex-col",
              showAICopilot ? "w-[60%] border-r" : "w-full",
              colors.borderPrimary
            )}>
              {/* Tab Navigation - Pill Style at TOP */}
              <div className="px-6 pt-4">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className={cn("mb-4 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                    <TabsTrigger 
                      value="key_attributes" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      Key Attributes
                    </TabsTrigger>
                    <TabsTrigger 
                      value="overview" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger 
                      value="health" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      Health
                    </TabsTrigger>
                    {isEnabled(FEATURE_IDS.TAB_GOALS) && isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
                    <TabsTrigger 
                      value="targets" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-targets"
                    >
                      <Target className="w-3 h-3 mr-1" />
                      KPIs {(() => {
                        // Tab count always shows current subscribed/selected count
                        const count = kpiOverrideData 
                          ? ephemeralKpiSelections.length 
                          : (formData.subscribed_kpi_ids?.length || 0);
                        return count > 0 ? `(${count})` : '';
                      })()}
                    </TabsTrigger>
                    )}
                    <TabsTrigger 
                      value="features" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      Features {(features.length > 0 || pendingChildUpdates.length > 0) && `(${features.length}${pendingChildUpdates.length > 0 ? `+${pendingChildUpdates.length}` : ''})`}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="attachments" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      <Paperclip className="w-3 h-3 mr-1" />
                      Attachments
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notes" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Notes
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">

                {/* ===== KEY ATTRIBUTES TAB ===== */}
                {activeTab === 'key_attributes' && (
                  <div className="space-y-5">
                    {/* Alert: Epic target date exceeds Initiative target date */}
                    {formData.target_date && epicDetails?.initiative?.target_date && 
                     new Date(formData.target_date) > new Date(epicDetails.initiative.target_date) && (
                      <div className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border",
                        "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700"
                      )}>
                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            Target date exceeds Initiative deadline
                          </p>
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                            This Epic&apos;s target date ({new Date(formData.target_date).toLocaleDateString()}) is after the parent Initiative&apos;s target date ({new Date(epicDetails.initiative.target_date).toLocaleDateString()}).
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Key Attributes Fields */}
                    <div className="space-y-4">
                      {/* Row 1: Epic Name & Status */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={colors.textSecondary}>Epic Name</Label>
                          {isEditing ? (
                            <Input
                              value={formData.title}
                              onChange={(e) => handleChange('title', e.target.value)}
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            />
                          ) : (
                            <p className={cn("mt-1 text-lg font-medium", colors.textPrimary)}>{formData.title || 'Untitled Epic'}</p>
                          )}
                        </div>
                        <div>
                          <Label className={colors.textSecondary}>Status</Label>
                          {isEditing ? (
                            <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                                {STATUS_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <div className="flex items-center gap-2">
                                      <div className={cn("w-2 h-2 rounded-full", opt.color)} />
                                      {opt.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="mt-1">
                              <Badge variant="outline" className={cn(
                                formData.status === 'shipped' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                formData.status === 'in_progress' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                                formData.status === 'planning' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                formData.status === 'paused' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                              )}>
                                {STATUS_OPTIONS.find(o => o.value === formData.status)?.label || formData.status}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Initiative & Target Date */}
                      <div className={cn("grid gap-4", isEnabled(FEATURE_IDS.TAB_INITIATIVES) ? "grid-cols-2" : "grid-cols-1")}>
                        {isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
                        <div>
                          <Label className={cn(
                            colors.textSecondary,
                            (formData.initiative_id || '') !== (epicDetails?.initiative_id || '') && "text-amber-600 dark:text-amber-400"
                          )}>
                            Initiative
                            {(formData.initiative_id || '') !== (epicDetails?.initiative_id || '') && (
                              <span className="ml-1 text-xs">(changed)</span>
                            )}
                          </Label>
                          {isEditing ? (
                            <Select 
                              value={formData.initiative_id || 'none'} 
                              onValueChange={(v) => handleChange('initiative_id', v === 'none' ? '' : v)}
                            >
                              <SelectTrigger className={cn(
                                "mt-1", 
                                colors.bgInput, 
                                colors.borderPrimary,
                                (formData.initiative_id || '') !== (epicDetails?.initiative_id || '') && "ring-2 ring-amber-400 dark:ring-amber-500"
                              )}>
                                <SelectValue placeholder="Select initiative" />
                              </SelectTrigger>
                              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                                <SelectItem value="none">Not linked</SelectItem>
                                {productContext?.initiatives?.map(init => (
                                  <SelectItem key={init.initiative_id} value={init.initiative_id}>
                                    {init.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className={cn("mt-1", colors.textPrimary)}>
                              {epicDetails?.initiative?.title || (
                                epicDetails?.initiative_deleted 
                                  ? <span className="text-amber-500 italic">Initiative was deleted</span>
                                  : 'Not linked'
                              )}
                            </p>
                          )}
                        </div>
                        )}
                        <div>
                          <Label className={colors.textSecondary}>Target Date</Label>
                          {isEditing ? (
                            <div className="mt-1">
                              <DatePickerWithMarkers
                                value={formData.target_date || ''}
                                onChange={(date) => handleChange('target_date', date)}
                                parentDeadlines={parentDeadlines}
                                placeholder="Select target date"
                              />
                            </div>
                          ) : (
                            <p className={cn("mt-1", colors.textPrimary)}>
                              {formData.target_date ? new Date(formData.target_date).toLocaleDateString() : 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== OVERVIEW TAB (Description & Problem Statement) ===== */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    {/* Personas - gated by feature flag */}
                    {isEnabled(FEATURE_IDS.TAB_PERSONAS) && (
                    <div>
                      <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                        <Users className="w-3 h-3" /> Personas
                      </Label>
                      <div className={cn(
                        "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border",
                        colors.bgInput, colors.borderPrimary,
                        !isEditing && "opacity-70"
                      )}>
                        {(formData.persona_ids || []).map(personaId => {
                          const persona = personas.find(p => p.persona_id === personaId);
                          return persona ? (
                            <span
                              key={personaId}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                            >
                              {persona.name}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleChange('persona_ids', formData.persona_ids.filter(id => id !== personaId))}
                                  className="ml-0.5 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          ) : null;
                        })}
                        {isEditing && (
                          <Select 
                            value=""
                            onValueChange={(v) => {
                              if (v && !formData.persona_ids?.includes(v)) {
                                handleChange('persona_ids', [...(formData.persona_ids || []), v]);
                              }
                            }}
                          >
                            <SelectTrigger className="w-auto min-w-[100px] h-7 text-xs border-0 bg-transparent focus:ring-0 p-0">
                              <SelectValue placeholder={formData.persona_ids?.length ? "Add more..." : "Add personas..."} />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              {personas
                                .filter(p => !formData.persona_ids?.includes(p.persona_id))
                                .map(p => (
                                  <SelectItem key={p.persona_id} value={p.persona_id}>{p.name}</SelectItem>
                                ))
                              }
                              {personas.filter(p => !formData.persona_ids?.includes(p.persona_id)).length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-gray-500">All personas selected</div>
                              )}
                            </SelectContent>
                          </Select>
                        )}
                        {(!formData.persona_ids || formData.persona_ids.length === 0) && !isEditing && (
                          <span className={cn("text-sm", colors.textMuted)}>No personas linked</span>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Description */}
                    <div>
                      <Label className={colors.textSecondary}>Description</Label>
                      {isEditing ? (
                        <div className="mt-1">
                          <MarkdownEditor
                            content={formData.description || ''}
                            onChange={(value) => handleChange('description', value)}
                            placeholder="Additional details about this epic..."
                            minHeight={150}
                            disabled={!isEditing}
                          />
                        </div>
                      ) : (
                        <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
                          {formData.description ? (
                            <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                              <ReactMarkdown>{formData.description}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className={cn("text-sm", colors.textMuted)}>No description</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Problem Statement */}
                    <div>
                      <Label className={colors.textSecondary}>Problem Statement</Label>
                      {isEditing ? (
                        <div className="mt-1">
                          <MarkdownEditor
                            content={formData.problem_statement || ''}
                            onChange={(value) => handleChange('problem_statement', value)}
                            placeholder="What problem does this epic solve? Why does it matter?"
                            minHeight={150}
                            disabled={!isEditing}
                          />
                        </div>
                      ) : (
                        <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
                          {formData.problem_statement ? (
                            <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                              <ReactMarkdown>{formData.problem_statement}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className={cn("text-sm", colors.textMuted)}>No problem statement defined yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* ===== KPI SUBSCRIPTIONS TAB (Outcome-Driven Model) ===== */}
                {activeTab === 'targets' && isEnabled(FEATURE_IDS.TAB_GOALS) && isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
                  loadingKpiOverride ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                      <span className={cn("ml-2", colors.textMuted)}>Loading KPIs for new Initiative...</span>
                    </div>
                  ) : (
                    <KpiSubscriptionTab
                      entityType="epic"
                      entityId={epic?.epic_id}
                      disabled={!isEditing}
                      fetchInheritedKpis={async (id) => {
                        const response = await epicsApi.getInheritedKpis(id);
                        return response.data;
                      }}
                      currentSubscriptions={
                        // When viewing different initiative, use ephemeral selections
                        // When viewing original initiative, show current form subscriptions
                        kpiOverrideData ? ephemeralKpiSelections : (formData.subscribed_kpi_ids || [])
                      }
                      onChange={(subscriptions) => {
                        if (kpiOverrideData) {
                          // Viewing different initiative - store ephemeral selections
                          setEphemeralKpiSelections(subscriptions);
                        } else {
                          // Viewing original initiative - update form data
                          setFormData(prev => ({
                            ...prev,
                            subscribed_kpi_ids: subscriptions
                          }));
                        }
                        setHasChanges(true);
                        setKpisModified(true);  // Track KPI change for cascade
                      }}
                      subscriptionFieldName="subscribed_kpi_ids"
                      overrideData={kpiOverrideData}
                    />
                  )
                )}

                {/* ===== HEALTH TAB ===== */}
                {activeTab === 'health' && (
                  <div className="space-y-5">
                    {health ? (
                      <>
                        {/* Progress */}
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className={colors.textSecondary}>Overall Progress</span>
                            <span className={cn("font-medium", colors.textPrimary)}>
                              {health.completed_features}/{health.total_features} features ({health.progress_percent}%)
                            </span>
                          </div>
                          <Progress value={health.progress_percent} className="h-3" />
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                            <p className={cn("text-sm", colors.textMuted)}>Effort</p>
                            <p className={cn("text-2xl font-bold mt-1", colors.textPrimary)}>
                              ~{health.total_effort}
                            </p>
                            <p className={cn("text-xs", colors.textMuted)}>story points</p>
                          </div>
                          <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                            <p className={cn("text-sm", colors.textMuted)}>Timeline</p>
                            <div className="flex items-center gap-2 mt-1">
                              {health.timeline_status === 'on_track' && <span className="text-2xl">🟢</span>}
                              {health.timeline_status === 'ahead' && <span className="text-2xl">🔵</span>}
                              {health.timeline_status === 'at_risk' && <span className="text-2xl">🟡</span>}
                              <p className={cn("font-medium capitalize", colors.textPrimary)}>
                                {health.timeline_status.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                          <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                            <p className={cn("text-sm", colors.textMuted)}>Scope</p>
                            {health.scope_increase > 0 ? (
                              <>
                                <p className="text-2xl font-bold mt-1 text-yellow-500">
                                  +{health.scope_increase}
                                </p>
                                <p className="text-xs text-yellow-600">
                                  {health.scope_increase_percent}% creep
                                </p>
                              </>
                            ) : (
                              <>
                                <p className={cn("text-2xl font-bold mt-1 text-green-500")}>
                                  Stable
                                </p>
                                <p className={cn("text-xs", colors.textMuted)}>No scope creep</p>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Feature Breakdown */}
                        <div>
                          <h4 className={cn("font-medium mb-3", colors.textPrimary)}>Feature Status Breakdown</h4>
                          <div className="space-y-2">
                            {['Done', 'In Progress', 'Review', 'Backlog'].map(status => {
                              const count = features.filter(f => f.status === status).length;
                              const percentage = features.length > 0 ? Math.round((count / features.length) * 100) : 0;
                              return (
                                <div key={status} className="flex items-center gap-3">
                                  <span className={cn("w-24 text-sm", colors.textMuted)}>{status}</span>
                                  <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div 
                                      className={cn(
                                        "h-full rounded-full",
                                        status === 'Done' ? 'bg-green-500' :
                                        status === 'In Progress' ? 'bg-blue-500' :
                                        status === 'Review' ? 'bg-purple-500' :
                                        'bg-gray-400'
                                      )}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className={cn("w-12 text-sm text-right", colors.textMuted)}>{count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className={cn("text-center py-8", colors.textMuted)}>
                        No health data available
                      </p>
                    )}
                  </div>
                )}

                {/* ===== FEATURES TAB ===== */}
                {activeTab === 'features' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className={cn("font-medium", colors.textPrimary)}>
                        Linked Features ({features.length})
                        {pendingChildUpdates.length > 0 && (
                          <span className="ml-1 text-amber-600 dark:text-amber-400 font-normal text-sm">
                            + {pendingChildUpdates.length} pending
                          </span>
                        )}
                      </h3>
                    </div>
                    
                    {features.length === 0 && pendingChildUpdates.length === 0 ? (
                      <p className={cn("text-center py-8", colors.textMuted)}>
                        No features linked to this epic yet.
                      </p>
                    ) : (
                      <div className={cn("rounded-lg border divide-y", colors.borderPrimary, "divide-gray-200 dark:divide-gray-700")} data-testid="features-list">
                        {features.map((feature) => (
                          <div
                            key={feature.feature_id}
                            className={cn(
                              "px-4 py-3 flex items-center justify-between transition-colors",
                              colors.textPrimary,
                              !suppressChildModals && onSelectFeature && "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            )}
                            onClick={() => !suppressChildModals && onSelectFeature?.(feature)}
                          >
                            <div className="flex items-center gap-3">
                              {feature.status === 'Done' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                              {feature.status === 'In Progress' && <Loader2 className="w-5 h-5 text-blue-500" />}
                              {feature.status === 'Review' && <Eye className="w-5 h-5 text-purple-500" />}
                              {feature.status === 'Backlog' && <Circle className={cn("w-5 h-5", colors.textMuted)} />}
                              <div>
                                <span className="font-medium">{feature.title}</span>
                                {feature.requirements?.length > 0 && (
                                  <span className={cn("ml-2 text-xs", colors.textMuted)}>
                                    {feature.requirements.length} requirements
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={cn(
                                feature.status === 'Done' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                feature.status === 'In Progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                              )}>
                                {feature.status}
                              </Badge>
                              {feature.effort_size && (
                                <span className={cn("text-xs px-2 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                                  {feature.effort_size}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {pendingChildUpdates.map((update) => (
                          <div
                            key={`pending-${update.item_id}`}
                            className={cn(
                              "px-4 py-3 flex items-center justify-between border-l-2 border-l-amber-400 dark:border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10 group",
                              colors.textPrimary
                            )}
                            data-testid={`pending-feature-${update.item_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <Link2 className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                              <div>
                                <span className="font-medium">{update.display_name}</span>
                                <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-600">
                                  pending
                                </Badge>
                                {update.reason && (
                                  <p className={cn("text-xs mt-0.5", colors.textMuted)}>{update.reason}</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setPendingChildUpdates(prev => prev.filter(u => u.item_id !== update.item_id))}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-amber-200 dark:hover:bg-amber-800 transition-opacity"
                              title="Remove this pending update"
                              data-testid={`remove-pending-${update.item_id}`}
                            >
                              <X className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ===== ATTACHMENTS TAB ===== */}
                {activeTab === 'attachments' && epic?.epic_id && (
                  <AttachmentsPanel
                    entityType="epic"
                    entityId={epic.epic_id}
                    productId={productContext?.product_id}
                    canEdit={true}
                    colors={colors}
                  />
                )}

                {/* ===== NOTES TAB ===== */}
                {activeTab === 'notes' && epic?.epic_id && (
                  <NotesPanel
                    entityType="epic"
                    entityId={epic.epic_id}
                    teamMembers={productContext?.team_members || []}
                    colors={colors}
                  />
                )}
              </div>
            </div>

            {/* Right Panel - AI Co-Pilot (40%) - Shown for draft status OR when in edit mode */}
            {showAICopilot && (
              <div className="w-[40%] flex flex-col">
                <UnifiedCoPilot
                  contextType="epic"
                  contextId={epic?.epic_id}
                  productId={productContext?.product_id}
                  contextData={{
                    ...formData,
                    // Include current KPI selection state (ephemeral or persisted)
                    subscribed_kpi_ids: kpiOverrideData 
                      ? ephemeralKpiSelections 
                      : formData.subscribed_kpi_ids
                  }}
                  relatedData={{
                    product: productContext,
                    personas: personas,
                    children: features,
                    parent: null, // Epics don't have a parent in this view
                    // Include current KPI context for AI awareness
                    kpi_context: {
                      available_kpis: kpiOverrideData?.inherited_kpis || epicDetails?.inherited_kpis || [],
                      selected_kpi_ids: kpiOverrideData 
                        ? ephemeralKpiSelections 
                        : (formData.subscribed_kpi_ids || []),
                      initiative_changed: (formData.initiative_id || '') !== (epicDetails?.initiative_id || ''),
                      viewing_initiative_id: formData.initiative_id || null
                    }
                  }}
                  onApplyUpdates={handleApplyUpdates}
                  canEditField={() => true} // Epics allow all field edits for now
                  onRefresh={refreshEpicData}
                  variant="item"
                />
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
            <div className={cn("relative w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
              <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Delete Epic?</h3>
              <p className={cn("text-sm mb-4", colors.textMuted)}>
                This will permanently delete this epic and unlink all its features. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" />Delete Epic</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Unsaved Changes Warning */}
        {showUnsavedWarning && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60" onClick={() => setShowUnsavedWarning(false)} />
            <div className={cn("relative w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
              <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Unsaved Changes</h3>
              <p className={cn("text-sm mb-4", colors.textMuted)}>
                You have unsaved changes. What would you like to do?
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowUnsavedWarning(false)}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUnsavedWarning(false);
                    performClose();
                  }}
                >
                  Discard Changes
                </Button>
                <Button
                  onClick={async () => {
                    await handleSave();
                    setShowUnsavedWarning(false);
                    performClose();
                  }}
                  className={colors.accentGradient}
                >
                  Save & Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </ModalZIndexProvider>
  );
};

export default EpicDetailModal;
